/**
 * ActiveMarkerSelectionManager
 * Manages active marker selection with hysteresis and lock mechanism
 */

// Default configuration
const DEFAULT_LOCK_DURATION = 800; // ms (T011)
const DEFAULT_HYSTERESIS = 50; // pixels (T010)

export class ActiveMarkerSelectionManager {
  /**
   * @param {Object} options - Configuration options
   * @param {number} options.lockDuration - Lock duration in ms (default: 800ms)
   * @param {number} options.hysteresis - Hysteresis threshold in pixels (default: 50px)
   */
  constructor(options = {}) {
    // Configuration
    this.lockDuration = options.lockDuration || DEFAULT_LOCK_DURATION;
    this.hysteresis = options.hysteresis || DEFAULT_HYSTERESIS;

    // State
    this.currentMarkerId = null;
    this.currentScore = null;
    this.lockExpiresAt = 0; // Timestamp when lock expires
    this.lastChangeAt = 0; // Timestamp of last change
    this.changeHistory = []; // History of marker changes

    console.log(
      `ActiveMarkerSelectionManager initialized: lockDuration=${this.lockDuration}ms, hysteresis=${this.hysteresis}px`
    );
  }

  /**
   * Update active marker selection
   * @param {string} candidateMarkerId - Candidate marker ID
   * @param {number} candidateScore - Candidate priority score (pixels, lower = better)
   * @returns {boolean} - True if selection changed
   */
  update(candidateMarkerId, candidateScore) {
    const now = performance.now();

    // If no candidate, clear selection
    if (!candidateMarkerId) {
      if (this.currentMarkerId) {
        console.log(`= [SELECTION] Clearing selection (no candidate)`);
        this.currentMarkerId = null;
        this.currentScore = null;
        this.lockExpiresAt = 0;
        return true;
      }
      return false;
    }

    // If no current selection, select candidate immediately
    if (!this.currentMarkerId) {
      console.log(
        `<� [SELECTION] Initial selection: ${candidateMarkerId} (score: ${candidateScore.toFixed(1)}px)`
      );
      this._select(candidateMarkerId, candidateScore, now);
      return true;
    }

    // If candidate is same as current, update score but no change
    if (candidateMarkerId === this.currentMarkerId) {
      this.currentScore = candidateScore;
      return false;
    }

    // T011: Check if selection is locked
    if (now < this.lockExpiresAt) {
      const lockRemaining = this.lockExpiresAt - now;
      console.log(
        `= [SELECTION LOCK] ${this.currentMarkerId} locked for ${lockRemaining.toFixed(0)}ms more`
      );
      return false;
    }

    // T010: Apply hysteresis - switch only if candidate is significantly closer
    const scoreDifference = this.currentScore - candidateScore;
    const shouldSwitch = scoreDifference > this.hysteresis;

    if (shouldSwitch) {
      console.log(
        `= [SELECTION SWITCH] ${this.currentMarkerId} � ${candidateMarkerId} (diff: ${scoreDifference.toFixed(1)}px > ${this.hysteresis}px)`
      );
      this._select(candidateMarkerId, candidateScore, now);
      return true;
    } else {
      console.log(
        `= [HYSTERESIS] Keep ${this.currentMarkerId} (diff: ${scoreDifference.toFixed(1)}px d ${this.hysteresis}px)`
      );
      return false;
    }
  }

  /**
   * Select a marker and apply lock
   * @param {string} markerId - Marker ID to select
   * @param {number} score - Priority score
   * @param {number} timestamp - Current timestamp
   * @private
   */
  _select(markerId, score, timestamp) {
    this.currentMarkerId = markerId;
    this.currentScore = score;
    this.lockExpiresAt = timestamp + this.lockDuration;
    this.lastChangeAt = timestamp;

    // Record change in history
    this.changeHistory.push({
      markerId,
      score,
      timestamp,
    });

    // Keep history limited to last 10 changes
    if (this.changeHistory.length > 10) {
      this.changeHistory.shift();
    }
  }

  /**
   * Get current active marker ID
   * @returns {string|null}
   */
  getCurrentMarkerId() {
    return this.currentMarkerId;
  }

  /**
   * Get current score
   * @returns {number|null}
   */
  getCurrentScore() {
    return this.currentScore;
  }

  /**
   * Check if selection is currently locked
   * @returns {boolean}
   */
  isLocked() {
    return performance.now() < this.lockExpiresAt;
  }

  /**
   * Get time remaining in lock (ms)
   * @returns {number} - Milliseconds remaining, 0 if not locked
   */
  getLockRemaining() {
    const remaining = this.lockExpiresAt - performance.now();
    return Math.max(0, remaining);
  }

  /**
   * Clear detected markers (when all markers lost)
   */
  clearDetectedMarkers() {
    console.log(`>� [SELECTION] Clearing all detected markers`);
    this.currentMarkerId = null;
    this.currentScore = null;
    this.lockExpiresAt = 0;
  }

  /**
   * Reset selection manager state
   */
  reset() {
    console.log(`= [SELECTION] Resetting selection manager`);
    this.currentMarkerId = null;
    this.currentScore = null;
    this.lockExpiresAt = 0;
    this.lastChangeAt = 0;
    this.changeHistory = [];
  }

  /**
   * Get current state
   * @returns {Object}
   */
  getState() {
    return {
      currentMarkerId: this.currentMarkerId,
      currentScore: this.currentScore,
      isLocked: this.isLocked(),
      lockRemaining: this.getLockRemaining(),
      lastChangeAt: this.lastChangeAt,
      changeCount: this.changeHistory.length,
    };
  }

  /**
   * Get change history
   * @returns {Array}
   */
  getChangeHistory() {
    return [...this.changeHistory];
  }

  /**
   * Update configuration
   * @param {Object} options - Configuration options
   */
  updateConfig(options = {}) {
    if (options.lockDuration !== undefined) {
      this.lockDuration = options.lockDuration;
      console.log(`=' [SELECTION] Lock duration updated: ${this.lockDuration}ms`);
    }

    if (options.hysteresis !== undefined) {
      this.hysteresis = options.hysteresis;
      console.log(`=' [SELECTION] Hysteresis updated: ${this.hysteresis}px`);
    }
  }
}
